import { useState, useCallback, useEffect } from 'react';
import api, { SocialMediaPost, SocialPostStatus } from '@/lib/api';

export function useSocialPosts(statusFilter?: SocialPostStatus) {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const loadPosts = useCallback(
    async (newOffset = 0) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await api.listSocialMediaPosts({
          status: statusFilter,
          limit,
          offset: newOffset,
        });
        if (newOffset === 0) {
          setPosts(result.posts);
        } else {
          setPosts((prev) => [...prev, ...result.posts]);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load posts');
      } finally {
        setIsLoading(false);
      }
    },
    [statusFilter],
  );

  useEffect(() => {
    loadPosts(0);
  }, [loadPosts]);

  const loadMore = useCallback(() => {
    const nextOffset = offset + limit;
    if (nextOffset < total) {
      loadPosts(nextOffset);
    }
  }, [offset, total, loadPosts]);

  const deletePost = useCallback(async (postId: string) => {
    await api.deleteSocialMediaPost(postId);
    setPosts((prev) => prev.filter((p) => p.postId !== postId));
    setTotal((prev) => prev - 1);
  }, []);

  const hasMore = offset + limit < total;

  return { posts, total, isLoading, error, loadPosts, loadMore, hasMore, deletePost };
}
